








































































































/* eslint-disable no-console */

import { Component, namespace, Ref, State } from 'nuxt-property-decorator';
import Highlighter from 'web-highlighter';

// @ts-ignore
import ToastService, { ToastType } from '../../toast/ToastService';
import ContextuallySortedList from '../ContextuallySortedList';

import { pageExistsInTree } from '../../highlight/utils/pageExists';
import CloseBtn from '../CloseBtn.vue';
import { NotesMode } from './NotesMode';
import NoteItem from './NoteItem.vue';
import { SortType } from './SortType';
import PdfButton from './PdfButton.vue';
import { scrollToTarget } from '@/util/scrollToTarget';
import { NoteHighlight, NoteState } from '@/types/highlightContract';
import { NoteMeta } from '@/types/noteMeta';
import { Navigation, Site } from '@/types/initialData';
import NotesStore from '@/store/notes.store';
import NotePreviewStore, { ClickedFrom } from '@/store/notePreview.store';
import SidePanelStore from '@/components/ui/side-panel/side-panel.store';
import LocalConfigStore from '@/store/localConfig.store';

const highlightsModule = namespace('highlights.store');
const webHighlighterStore = namespace('webHighlighter.store');

@Component({
  components: {
    PdfButton,
    CloseBtn,
    NoteItem
  }
})
export default class NotesList extends ContextuallySortedList {
  @highlightsModule.Action
  deleteHighlightFromPage: ({
    pid: string,
    site: Site,
    highlight: NoteHighlight
  }) => Promise<void>;

  @highlightsModule.Getter
  highlightsForPid: (pid: string) => NoteHighlight[];

  @highlightsModule.Getter
  highlightsForIsbn: NoteHighlight[];

  @webHighlighterStore.Getter
  currentId: string;

  @webHighlighterStore.Getter
  idMap: Map<String, NoteMeta>;

  @webHighlighterStore.Getter
  noteMap: Map<String, String>;

  @webHighlighterStore.Getter
  highlighter: Highlighter;

  @webHighlighterStore.Mutation
  setCurrentId: (id: string | null) => void;

  @highlightsModule.Mutation('updateCurrentHighlightId')
  updateCurrentHighlightId: (id: string) => void;

  // @ts-ignore
  @State((state) => state.typo3?.page?.page?.id) // notice : there's also id, and that reflects the query param
  pageId: number;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.site)
  site: Site;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.navigation?.[0] ?? [])
  navMain;

  // To allow using enum in vue template
  SortType = SortType;
  sorting: SortType = SortType.CHAPTER;
  top: number = 0;
  listIsInDom: boolean = false;

  get selectedHighlightId(): string | null {
    return this.idMap.get(this.currentId)?.highlight?.id;
  }

  get highlights(): NoteHighlight[] {
    const hls = NotesStore.showPageNotesOnly
      ? this.highlightsForPid(this.pageId.toString(10))
      : this.highlightsForIsbn;
    return this.sorting === SortType.CHAPTER
      ? hls?.sort(this.sortByChapter(this.sortedCIDList, this.sortedPidList)) ||
          []
      : this.sorting === SortType.NEWEST
      ? hls?.sort(this.sortByNewest()) || []
      : [];
  }

  get pageTransitionReady(): boolean {
    return LocalConfigStore.pageTransitionReady;
  }

  get sortedCIDList(): string[] {
    const cidElements = document.querySelectorAll('[id^="c"]');
    const cidList = [];
    cidElements.forEach((cidElement) => {
      cidList.push(cidElement.id);
    });
    return cidList;
  }

  get sortedPidList(): number[] {
    const orderedPids = [];

    const queue: Navigation[] = [];
    queue.push(this.navMain);

    let link;
    while ((link = queue.pop())) {
      if (link && link.uid) {
        orderedPids.push(link.uid);
      }
      if (link.children && link.children.length) {
        // Ensure to push children in reverse, as we traverse them with .pop from last to first
        for (let i = link.children.length - 1; i >= 0; --i) {
          queue.push(link.children[i]);
        }
      }
    }

    return orderedPids;
  }

  @Ref()
  previewHighlight: HTMLElement;

  // The overflow property we manipulate on the html, is to prevent the ui jump when opening the list
  // We remember to 'unset' when the list is visible and when we destroy the list
  mounted() {
    const htmlElement = document.documentElement;
    htmlElement.style.overflow = 'hidden';
    this.$nextTick(() => {
      this.listIsInDom = true;
    });
  }

  beforeDestroy() {
    const htmlElement = document.documentElement;
    htmlElement.style.overflow = 'unset';
  }

  listReady(): void {
    this.handleScrollTopToList();
  }

  handleScrollTopToList(): void {
    if (this.$el.scrollHeight > 0) {
      const rightDrawer = document.querySelector('.js-right-drawer');
      const htmlElement = document.documentElement;
      htmlElement.style.overflow = 'unset';

      rightDrawer.scrollTop = 0;
    }
  }

  async deleteHighlight(highlight: NoteHighlight) {
    const webhlId = this.noteMap.get(highlight.id);

    try {
      await this.deleteHighlightFromPage({
        pid: highlight.pid,
        site: this.site,
        highlight
      });
      // console.log(`Removed highlight.`);
      if (this.idMap.get(webhlId)?.isHighlight) {
        this.highlighter.remove(webhlId);
        NotesStore.deletePermanentHighlightMarker(highlight);
        this.idMap.delete(webhlId);
        this.noteMap.delete(highlight.id);
        NotePreviewStore.closePreview();
      }
      ToastService.showToast(
        this.$i18n.t('labels.notes.deleted') as string,
        ToastType.Success
      );
    } catch (e) {
      console.error('Error removing highlight', e);
    }
  }

  pageExists(pid: number): boolean {
    return pageExistsInTree(this.navMain, pid);
  }

  navigateToHighlight(highlight: NoteHighlight) {
    const webHlId = this.noteMap.get(highlight.id);
    const currentPId = this.$route.query.id;
    // We only take the first occurrence, to navigate to a approximate destination
    const highlightDom = this.highlighter.getDoms(webHlId)[0];
    const highlightPId = highlight.pid.toString();

    // Remember to close side panel on mobile
    if (this.$vuetify.breakpoint.smAndDown) {
      SidePanelStore.rightPanelIsOpen = false;
    }

    if (!this.pageExists(Number(highlightPId))) {
      ToastService.showToast(
        this.$i18n.t('labels.notes.no-such-text') as string,
        ToastType.Warning
      );
      return;
    }

    this.updateCurrentHighlightId(highlight.id);
    this.$router.push({
      query: { id: highlight.pid },
      hash: '#c' + highlight.recreationData.startCID
    });
    NotePreviewStore.closePreview();
  }

  showCurrentPreview(highlight: NoteHighlight, event: any): void {
    // Prevent showing preview on mobile devices
    if (this.$vuetify.breakpoint.smAndDown) {
      return;
    }

    if (highlight) {
      const noteMetaId = this.noteMap.get(highlight.id);
      if (noteMetaId) {
        this.setCurrentId(noteMetaId);
      } else {
        this.createMetaDataForNonRenderedHighlight(highlight);
      }
      NotePreviewStore.showHighlightPreview(event, ClickedFrom.NOTES_LIST);
    } else {
      console.warn('Could not show preview of note.', highlight, event);
    }
  }

  createMetaDataForNonRenderedHighlight(highlight: NoteHighlight) {
    const noteMeta: NoteMeta = {
      cid: highlight.recreationData.startCID,
      highlight,
      isHighlight: false,
      noteState: NoteState.LOCAL,
      pid: Number(highlight.pid) || -1,
      webHighlighterId: null
    };

    this.idMap.set(highlight.id, noteMeta);
    this.setCurrentId(highlight.id);
  }

  updateHighlight(highlight: NoteHighlight) {
    const id = this.noteMap.get(highlight.id);
    if (id) {
      this.setCurrentId(this.noteMap.get(highlight.id));
    } else {
      // This just means, that there has not been created a highlight for the note, but it still exists!
      // @ts-ignore
      this.createMetaDataForNonRenderedHighlight(highlight);
    }

    NotesStore.setNotesMode(NotesMode.EDIT);
  }

  closeList(): void {
    SidePanelStore.rightPanelIsOpen = false;
  }

  private sortByNewest() {
    return (a: NoteHighlight, b: NoteHighlight) => {
      return b.createdTimestamp - a.createdTimestamp;
    };
  }

  private handleScroll(highlightDom): void {
    if (this.pageTransitionReady) {
      const header = document.getElementById('js-app-header');
      const headerOffset = header.offsetHeight + 20;
      scrollToTarget(highlightDom, 300, headerOffset);
    } else {
      setTimeout(() => this.handleScroll(highlightDom), 16);
    }
  }

  // Sort by undefined to the end, pid > cid > location in cid (if applicable)
  private sortByChapter(idList: string[], sortedPidList: number[]) {
    return (a: NoteHighlight, b: NoteHighlight) => {
      const aMeta = this.idMap.get(this.noteMap.get(a.id));
      const bMeta = this.idMap.get(this.noteMap.get(b.id));

      if (a.pid === b.pid) {
        // We end up in a case where we can't get location info for all pages / cids, so there is no chance we can sort correctly.
        // Sort by newest inside of each pid-group instead.
        // Outcommented, because: Mikkel: Sort by cid regardless
        // if (!this.showOnlyForPage) {
        //   return this.sortByNewest()(a, b);
        // }

        // Put items without a CID in the end
        if (typeof aMeta === 'undefined' || !aMeta) {
          // The warnings occur when we have page only notes
          // console.warn('no metadata for a');
          return 1;
        }
        if (typeof bMeta === 'undefined' || !bMeta) {
          // The warnings occur when we have page only notes
          // console.warn('no metadata for b');
          return 1;
        }

        const aCid = aMeta.cid;
        const bCid = bMeta.cid;

        const aLoc = idList.indexOf(aCid);
        const bLoc = idList.indexOf(bCid);

        if (aLoc === bLoc) {
          if (a.ranges.length > 0 && b.ranges.length > 0) {
            return a.ranges[0]?.startOffset - b.ranges[0]?.startOffset;
          }
        }
        return aLoc - bLoc;
      } else {
        const aPidLoc = sortedPidList.indexOf(Number(a.pid));
        const bPidLoc = sortedPidList.indexOf(Number(b.pid));

        // always in the back if the pid doesn't exist anymore.
        if (aPidLoc === -1 || bPidLoc === -1) {
          return 1;
        }

        return aPidLoc - bPidLoc;
      }
    };
  }
}
